import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  ElementRef,
  inject,
  input,
  OnChanges,
  OnInit,
  signal,
  SimpleChanges,
  viewChild,
  viewChildren,
} from '@angular/core';
import { ImageComponent } from '@shared/components/image';
import { Instagram, InstagramService } from '@shared/components/instagram';
import { IconsDirective } from '@shared/directives';
import { Image } from '@shared/models';
import { fromEvent, map, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';

@Component({
  selector: 'app-instagram-dialog',
  imports: [IconsDirective, AsyncPipe, ImageComponent],
  templateUrl: './instagram-dialog.component.html',
  styleUrl: './instagram-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InstagramDialogComponent implements OnInit, OnChanges {
  readonly flux = input.required<Instagram>();
  readonly widthChange = input.required<number>();
  readonly instagramWrapper = viewChild.required<ElementRef<HTMLElement>>('instagramWrapper');
  readonly picture = viewChild.required<ElementRef<HTMLElement>>('picture');
  readonly prevBtn = viewChild.required<ElementRef<HTMLElement>>('prevBtn');
  readonly nextBtn = viewChild.required<ElementRef<HTMLElement>>('nextBtn');
  readonly contentWrapper = viewChild.required<ElementRef<HTMLElement>>('contentWrapper');
  readonly internalCarouselInner = viewChild.required<ElementRef<HTMLElement>>('internalCarouselInner');
  readonly internalItem = viewChildren<ElementRef<HTMLElement>>('internalItem');

  pictureWidth = signal(0);
  imageReady = signal(false);
  selectedInternalItemIndex = signal(0);
  #subscription: Subscription = new Subscription();
  #currentScrollPosition = signal(0);
  #internalItemsPosition = signal<number[]>([]);
  readonly #document = inject(DOCUMENT);
  readonly #instagramService = inject(InstagramService);
  readonly isOpen$ = this.#instagramService.isDialogOpened$().pipe(
    tap((isOpen) => {
      if (isOpen) {
        setTimeout(() => {
          this.#subscription = fromEvent(this.#document, 'click')
            .pipe(tap((event: Event) => this.#handleDocumentClick(event)))
            .subscribe();
          const pictureWidth = this.picture().nativeElement.clientWidth;
          if (pictureWidth > 0) {
            this.pictureWidth.set(pictureWidth);
            this.imageReady.set(true);
          }
        });
      } else {
        this.#subscription.unsubscribe();
      }
    })
  );
  #selectedItemIndex = signal(0);
  readonly selectedItem$ = this.#instagramService.getSelectedItem$().pipe(
    map((index) => {
      if (index === null) {
        return null;
      } else {
        this.#selectedItemIndex.set(index);
        const itemSelected = this.flux().items[index];
        if (itemSelected && itemSelected.images.length > 1) {
          this.#setParametersForInternalCarousel(itemSelected.images);
        }
        return this.flux().items[index];
      }
    })
  );
  #maxItemIndex = 0;

  ngOnInit(): void {
    this.#maxItemIndex = this.flux().items.length - 1;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['widthChange'] && !changes['widthChange'].firstChange) {
      this.#instagramService.setSelectedItemIndex(this.#selectedItemIndex());
      const pictureWidth = this.picture().nativeElement.clientWidth;
      if (pictureWidth > 0) {
        this.pictureWidth.set(pictureWidth);
      }
    }
  }

  closeDialog(): void {
    this.selectedInternalItemIndex.set(0);
    this.#instagramService.closeDialog();
  }

  prevItem(): void {
    this.selectedInternalItemIndex.set(0);
    this.#instagramService.setSelectedItemIndex(
      this.#selectedItemIndex() === 0 ? this.#maxItemIndex : this.#selectedItemIndex() - 1
    );
    this.contentWrapper().nativeElement.scrollTop = 0;
  }

  nextItem(): void {
    this.selectedInternalItemIndex.set(0);
    this.#instagramService.setSelectedItemIndex(
      this.#selectedItemIndex() === this.#maxItemIndex ? 0 : this.#selectedItemIndex() + 1
    );
    this.contentWrapper().nativeElement.scrollTop = 0;
  }

  scrollPrev(): void {
    if (this.#currentScrollPosition() === 0) {
      this.selectedInternalItemIndex.set(this.#internalItemsPosition().length - 1);
      this.#currentScrollPosition.set(this.#internalItemsPosition()[this.#internalItemsPosition().length - 1]!);
    } else {
      this.selectedInternalItemIndex.set(this.selectedInternalItemIndex() - 1);
      this.#currentScrollPosition.set(
        this.#currentScrollPosition() - this.internalItem().at(0)!.nativeElement.clientWidth
      );
    }
    this.internalCarouselInner().nativeElement.scrollTo({ left: this.#currentScrollPosition(), behavior: 'smooth' });
  }

  scrollNext(): void {
    if (this.#currentScrollPosition() === this.#internalItemsPosition()[this.#internalItemsPosition().length - 1]) {
      this.selectedInternalItemIndex.set(0);
      this.#currentScrollPosition.set(0);
    } else {
      this.selectedInternalItemIndex.set(this.selectedInternalItemIndex() + 1);
      this.#currentScrollPosition.set(
        this.#currentScrollPosition() + this.internalItem().at(0)!.nativeElement.clientWidth
      );
    }

    this.internalCarouselInner().nativeElement.scrollTo({ left: this.#currentScrollPosition(), behavior: 'smooth' });
  }

  selectInternalItem(itemIndex: number): void {
    this.selectedInternalItemIndex.set(itemIndex);
    this.#currentScrollPosition.set(this.#internalItemsPosition()[itemIndex]!);
    this.internalCarouselInner().nativeElement.scrollTo({ left: this.#currentScrollPosition(), behavior: 'smooth' });
  }

  resetSelectedInternalItemIndex(): void {
    setTimeout(() => {
      const scrollLeft = this.internalCarouselInner().nativeElement.scrollLeft;
      this.#currentScrollPosition.set(
        this.#internalItemsPosition().reduce((prev, curr) =>
          Math.abs(curr - scrollLeft) < Math.abs(prev - scrollLeft) ? curr : prev
        )
      );
      this.selectedInternalItemIndex.set(
        this.#internalItemsPosition().findIndex((item) => item === this.#currentScrollPosition()) ?? 0
      );
    }, 250);
  }

  #handleDocumentClick(event: Event): void {
    if (
      !this.instagramWrapper().nativeElement.contains(event.target as Node) &&
      !this.prevBtn().nativeElement.contains(event.target as Node) &&
      !this.nextBtn().nativeElement.contains(event.target as Node)
    ) {
      this.closeDialog();
    }
  }

  #setParametersForInternalCarousel(images: Image[]): void {
    this.#currentScrollPosition.set(0);
    this.#buildInternalItemPosition(images.length);
  }

  #buildInternalItemPosition(nbItems: number): void {
    setTimeout(() => {
      this.selectedInternalItemIndex.set(0);
      this.#internalItemsPosition.set([]);
      const size = this.picture().nativeElement.clientWidth;
      for (let i = 0; i < nbItems; i++) {
        this.#internalItemsPosition.set([...this.#internalItemsPosition(), size * i]);
      }
    }, 500);
  }
}
